const SUPPORT_PORTAL_ROLES = new Set([
  'ROLE_THERAPY_SUP_L1',
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_THERAPIST_ADMIN',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_CLINIC_ROSTER_MANAGER',
  'ROLE_CLINIC_ROSTER_ADMIN',
  'ROLE_SUPPORT',
  'ROLE_SUPPORT_USER',
  'ROLE_PAYMENT_SUPPORT',
  'ROLE_MANAGEMENT',
])

const MENTOR_PORTAL_ROLES = new Set([
  'ROLE_DOCTOR',
  'ROLE_TEACHER',
  'ROLE_CREATOR',
  'ROLE_THERAPIST',
  'ROLE_SCREENER',
])

const MENTOR_CLINIC_ACCESS_ROLES = new Set([
  'ROLE_DOCTOR',
  'ROLE_THERAPIST',
  'ROLE_SCREENER',
])

const CLINIC_PORTAL_ROLES = new Set([
  'ROLE_CLINIC_OWNER',
  'ROLE_CLINIC_MANAGER',
  'ROLE_CLINIC_PROSPECT',
])

const BUSINESS_PORTAL_ROLES = new Set([
  'ROLE_BUSINESS_CUSTOMER',
  'ROLE_BUSINESS_PROSPECT',
])

function hasAnyRole(userRoles, candidateRoles) {
  return userRoles.some((role) => candidateRoles.has(role))
}

function parseJsonSafe(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

export function normalizeAuthorities(authoritiesLike) {
  if (Array.isArray(authoritiesLike)) {
    return authoritiesLike.filter((value) => typeof value === 'string' && value.trim())
  }

  if (typeof authoritiesLike === 'string') {
    const parsedAuthorities = parseJsonSafe(authoritiesLike)
    return Array.isArray(parsedAuthorities)
      ? parsedAuthorities.filter((value) => typeof value === 'string' && value.trim())
      : []
  }

  return []
}

export function resolveDirectPortalHandoff(authoritiesLike, options = {}) {
  const userRoles = normalizeAuthorities(authoritiesLike)

  if (userRoles.length === 0) {
    return null
  }

  const lastChosenFlow =
    typeof options?.lastChosenFlow === 'string' ? options.lastChosenFlow.trim() : ''
  const kioskConfig = options?.kioskConfig || null
  const roleCaConfig = options?.roleCaConfig || null
  const hasSupportPortal = hasAnyRole(userRoles, SUPPORT_PORTAL_ROLES)
  const hasMentorPortal = hasAnyRole(userRoles, MENTOR_PORTAL_ROLES)
  const hasClinicPortal = hasAnyRole(userRoles, CLINIC_PORTAL_ROLES)
  const hasBusinessPortal = hasAnyRole(userRoles, BUSINESS_PORTAL_ROLES)
  const hasClinicAssistantPortal = userRoles.includes('ROLE_CLINIC_ASSISTANT')
  const hasKioskPortal = userRoles.includes('ROLE_AUMHUM_SCREEN')

  if (hasClinicAssistantPortal && hasKioskPortal) {
    if (lastChosenFlow === 'kiosk') {
      return { portalId: 'kiosk', path: '/kiosk' }
    }

    if (lastChosenFlow === 'clinic-assistant') {
      return { portalId: 'clinic-assistant', path: '/ca-home' }
    }

    if (kioskConfig?.FLOW_ID) {
      return { portalId: 'kiosk', path: '/kiosk' }
    }

    if (roleCaConfig?.FLOW_ID) {
      return { portalId: 'clinic-assistant', path: '/ca-home' }
    }

    return null
  }

  const availablePortalIds = [
    hasSupportPortal ? 'support' : null,
    hasMentorPortal ? 'mentor' : null,
    hasClinicPortal ? 'clinic' : null,
    hasBusinessPortal ? 'business' : null,
    hasClinicAssistantPortal ? 'clinic-assistant' : null,
    hasKioskPortal ? 'kiosk' : null,
  ].filter(Boolean)

  if (availablePortalIds.length !== 1) {
    return null
  }

  switch (availablePortalIds[0]) {
    case 'support':
      return { portalId: 'support', path: '/support-home' }
    case 'mentor':
      return {
        portalId: 'mentor',
        path: hasAnyRole(userRoles, MENTOR_CLINIC_ACCESS_ROLES)
          ? '/mentor-home/my-clinic-associations'
          : '/mentor-home/preferences-and-settings',
      }
    case 'clinic':
      return { portalId: 'clinic', path: '/clinic-home' }
    case 'business':
      return { portalId: 'business', path: '/workplace' }
    case 'clinic-assistant':
      return { portalId: 'clinic-assistant', path: '/ca-home' }
    case 'kiosk':
      return { portalId: 'kiosk', path: '/kiosk' }
    default:
      return null
  }
}

export function rememberPortalHandoff(portalId) {
  if (typeof window === 'undefined' || typeof portalId !== 'string' || !portalId.trim()) {
    return
  }

  sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
  localStorage.setItem('lastChosenFlow', portalId)
}

export function shouldSkipSecureSetupOnPortalRoutingPage(authoritiesLike, options = {}) {
  const pathname = typeof options?.pathname === 'string' ? options.pathname.trim() : ''
  const explicitlyChoseConsumerPortal = options?.explicitlyChoseConsumerPortal === true

  if (pathname !== '/' && pathname !== '') {
    return false
  }

  if (explicitlyChoseConsumerPortal) {
    return false
  }

  return Boolean(resolveDirectPortalHandoff(authoritiesLike, options))
}
